'use client';

import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Check, Flag, Minus, Plus, RotateCcw } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { TimeSlot } from './scheduleBlockUtils';

export interface TimeSlotChipProps {
  slot: TimeSlot;
  courtCount: number;
  maxCourts: number;
  onCountChange: (nextCount: number) => void;
  /** If marked with check: syncs with other checked slots and violet style. */
  isChecked?: boolean;
  onToggleCheck?: () => void;
  /** Drag to select range: do not start drag if click is on a button. */
  onMouseDown?: (e: React.MouseEvent) => void;
  onMouseEnter?: (e: React.MouseEvent) => void;
  /** Visual preview while dragging the range. */
  isDragPreview?: boolean;
  disabled?: boolean;
  className?: string;
  /** SHORT knockout cut: 'start' is the cut slot, 'after' a slot played as bracket. */
  knockoutRole?: 'start' | 'after';
  onToggleKnockoutStart?: () => void;
}

/**
 * Slot chip: time at top and counter - X + below for courts.
 */
export function TimeSlotChip({
  slot,
  courtCount,
  maxCourts,
  onCountChange,
  isChecked = false,
  onToggleCheck,
  onMouseDown: onMouseDownProp,
  onMouseEnter: onMouseEnterProp,
  isDragPreview = false,
  disabled = false,
  className,
  knockoutRole,
  onToggleKnockoutStart,
}: TimeSlotChipProps) {
  const handleMouseDown = (e: React.MouseEvent) => {
    if (
      e.target instanceof HTMLElement &&
      (e.target.closest('button') || e.target.closest('input') || e.target.closest('[data-slot-controls]'))
    )
      return;
    onMouseDownProp?.(e);
  };
  const [inputValue, setInputValue] = useState(String(courtCount));
  const inputRef = useRef<HTMLInputElement>(null);
  const displayLabel = `${slot.startTime} - ${slot.endTime}`;
  const hasCourts = courtCount > 0;
  const isSelected = hasCourts || isChecked;
  const knockoutFlagLabel =
    knockoutRole === 'start' ? 'Quitar el corte de llaves' : 'Las llaves arrancan acá';

  useEffect(() => {
    if (document.activeElement !== inputRef.current) setInputValue(String(courtCount));
  }, [courtCount]);

  const clamp = (n: number) => Math.min(maxCourts, Math.max(0, Math.floor(n)));
  const applyInput = () => {
    const n = parseInt(inputValue, 10);
    if (!isNaN(n)) {
      const c = clamp(n);
      onCountChange(c);
      setInputValue(String(c));
    } else setInputValue(String(courtCount));
  };

  const handlePlus = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;
    const next = clamp(courtCount + 1);
    onCountChange(next);
    setInputValue(String(next));
  };
  const handleMinus = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;
    const next = clamp(courtCount - 1);
    onCountChange(next);
    setInputValue(String(next));
  };
  const handleReset = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;
    onCountChange(0);
    setInputValue('0');
  };

  return (
    <div
      className={cn(
        'relative flex min-h-[3rem] select-none flex-col items-center justify-center rounded-lg border px-3 py-2 pt-7 text-center text-sm transition-all',
        'border-border bg-muted/30 text-muted-foreground',
        isSelected && !isChecked && 'border-green-600 text-green-800',
        isChecked && 'border-violet-600 text-violet-800',
        knockoutRole && !isChecked && 'border-amber-500 bg-amber-50 text-amber-800',
        isDragPreview && 'border-violet-400 border-2 border-dashed bg-violet-100',
        disabled && 'pointer-events-none opacity-50',
        className,
      )}
      onMouseDown={onMouseDownProp ? handleMouseDown : undefined}
      onMouseEnter={onMouseEnterProp}
    >
      {onToggleCheck && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (!disabled) onToggleCheck();
          }}
          className={cn(
            'absolute left-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-md border transition-colors',
            isChecked
              ? 'border-violet-600 bg-violet-600 text-white'
              : 'border-border bg-background hover:border-violet-400 hover:bg-violet-50',
          )}
          title={isChecked ? 'Desmarcar (dejar de sincronizar)' : 'Marcar para sincronizar con otros'}
        >
          {isChecked && <Check className="h-3.5 w-3.5" />}
        </button>
      )}
      {onToggleKnockoutStart && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (!disabled) onToggleKnockoutStart();
          }}
          title={knockoutFlagLabel}
          aria-label={knockoutFlagLabel}
          className={cn(
            'absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-md border transition-colors',
            knockoutRole === 'start'
              ? 'border-amber-600 bg-amber-500 text-white'
              : 'border-amber-400 bg-background text-amber-600 hover:bg-amber-100',
          )}
        >
          <Flag className={cn('h-3.5 w-3.5', knockoutRole === 'start' && 'fill-current')} />
        </button>
      )}
      <span
        className={cn(
          'whitespace-nowrap text-xs sm:text-sm',
          isSelected ? 'font-bold' : 'font-medium',
          isSelected && !isChecked && 'text-green-800',
          isChecked && 'text-violet-800',
          knockoutRole && !isChecked && 'text-amber-800',
        )}
      >
        {displayLabel}
      </span>
      {knockoutRole === 'start' && (
        <span className="text-[10px] font-semibold">Llaves desde acá</span>
      )}
      <div
        className="mt-1 flex items-center justify-center gap-0.5 sm:mt-1.5 sm:gap-1"
        data-slot-controls
        onMouseDown={(e) => e.stopPropagation()}
      >
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-6 w-6 shrink-0 sm:h-7 sm:w-7"
          onClick={handleMinus}
          aria-label="Restar cancha"
          disabled={disabled || courtCount <= 0}
        >
          <Minus className="h-3 w-3 sm:h-4 sm:w-4" />
        </Button>
        <Input
          ref={inputRef}
          type="number"
          min={0}
          max={maxCourts}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onBlur={applyInput}
          onKeyDown={(e) => {
            if (e.key === 'Enter') applyInput();
          }}
          className={cn(
            'h-6 w-8 shrink-0 px-1 text-center text-xs tabular-nums transition-colors sm:h-7 sm:w-10',
            '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
            hasCourts && 'font-bold',
            hasCourts && !isChecked && 'text-green-800',
            hasCourts && isChecked && 'text-violet-800',
          )}
          onClick={(e) => e.stopPropagation()}
        />
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-6 w-6 shrink-0 sm:h-7 sm:w-7"
          onClick={handlePlus}
          disabled={disabled || courtCount >= maxCourts}
          // Icon-only: without a name this button is unreachable for screen
          // readers, and the reset button beside it already carries a title.
          aria-label="Sumar cancha"
        >
          <Plus className="h-3 w-3 sm:h-4 sm:w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-6 w-6 shrink-0 text-muted-foreground hover:text-destructive sm:h-7 sm:w-7"
          onClick={handleReset}
          disabled={disabled || courtCount <= 0}
          title="Resetear"
        >
          <RotateCcw className="h-3 w-3 sm:h-4 sm:w-4" />
        </Button>
      </div>
    </div>
  );
}
